<script setup lang="ts">
import { reactive } from 'vue'
import { Button, Card, TextInput, SelectInput } from 'ui-kit'

export interface SchoolCreateFormData {
  name: string
  full_name: string
  is_production: boolean
  use_sections: boolean
  admin_email: string
  admin_password: string
  admin_first_name: string
  admin_last_name: string
}

interface Props {
  initialData?: Partial<SchoolCreateFormData>
  submitLabel?: string
  isPending?: boolean
  error?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  submitLabel: 'Create School',
  isPending: false,
  error: null,
})

const emit = defineEmits<{
  (e: 'submit', data: SchoolCreateFormData): void
  (e: 'cancel'): void
}>()

const form = reactive<SchoolCreateFormData>({
  name: props.initialData?.name ?? '',
  full_name: props.initialData?.full_name ?? '',
  is_production: props.initialData?.is_production ?? false,
  use_sections: props.initialData?.use_sections ?? false,
  admin_email: props.initialData?.admin_email ?? '',
  admin_password: props.initialData?.admin_password ?? '',
  admin_first_name: props.initialData?.admin_first_name ?? '',
  admin_last_name: props.initialData?.admin_last_name ?? '',
})

const productionOptions = [
  { value: false, text: 'No' },
  { value: true, text: 'Yes' },
]

const sectionsOptions = [
  { value: false, text: 'No' },
  { value: true, text: 'Yes' },
]

function handleSubmit() {
  emit('submit', { ...form })
}
</script>

<template>
  <Card>
    <form class="school-create-form" @submit.prevent="handleSubmit">
      <fieldset class="school-create-form__section">
        <legend class="school-create-form__section-title">School Information</legend>

        <TextInput
          v-model="form.name"
          label="School Name"
          placeholder="Enter short school name"
          required
        />

        <TextInput
          v-model="form.full_name"
          label="Full Name"
          placeholder="Enter full school name"
          required
        />

        <SelectInput
          v-model="form.is_production"
          label="Is Production"
          :options="productionOptions"
          required
        />

        <SelectInput
          v-model="form.use_sections"
          label="Use Sections"
          :options="sectionsOptions"
          required
        />
      </fieldset>

      <fieldset class="school-create-form__section">
        <legend class="school-create-form__section-title">Initial Admin User</legend>

        <TextInput
          v-model="form.admin_first_name"
          label="First Name"
          placeholder="Enter admin first name"
          required
        />

        <TextInput
          v-model="form.admin_last_name"
          label="Last Name"
          placeholder="Enter admin last name"
          required
        />

        <TextInput
          v-model="form.admin_email"
          label="Email"
          type="email"
          placeholder="admin@school.edu"
          required
        />

        <TextInput
          v-model="form.admin_password"
          label="Password"
          type="password"
          placeholder="Enter initial password"
          required
        />
      </fieldset>

      <div v-if="error" class="school-create-form__error">
        {{ error }}
      </div>

      <div class="school-create-form__actions">
        <Button variant="outline" type="button" @click="emit('cancel')">
          Cancel
        </Button>
        <Button variant="default" type="submit" :disabled="isPending">
          {{ isPending ? 'Submitting...' : submitLabel }}
        </Button>
      </div>
    </form>
  </Card>
</template>

<style scoped>
.school-create-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.school-create-form__section {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.school-create-form__section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1c1917;
  margin-bottom: 4px;
  padding: 0;
}

.school-create-form__error {
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
}

.school-create-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #e7e5e4;
}
</style>
